import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Check, X, AlertTriangle, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface SmartButtonProps {
  children: React.ReactNode;
  onClick?: () => void | Promise<void>;
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  size?: "default" | "sm" | "lg" | "icon";
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  
  // Smart features
  requireConfirmation?: boolean;
  confirmationTitle?: string;
  confirmationDescription?: string;
  successMessage?: string;
  showSuccessState?: boolean;
  
  // Icons
  icon?: React.ComponentType<{ className?: string }>;
  loadingIcon?: React.ComponentType<{ className?: string }>;
  successIcon?: React.ComponentType<{ className?: string }>;
}

export const SmartButton: React.FC<SmartButtonProps> = ({
  children,
  onClick,
  variant = "default",
  size = "default",
  disabled = false,
  loading: externalLoading = false,
  className,
  
  // Smart features
  requireConfirmation = false,
  confirmationTitle = "تأكيد العملية",
  confirmationDescription = "هل أنت متأكد من تنفيذ هذا الإجراء؟",
  successMessage,
  showSuccessState = false,
  
  // Icons
  icon: Icon,
  loadingIcon: LoadingIcon = LoadingSpinner,
  successIcon: SuccessIcon = Check,
}) => {
  const [internalLoading, setInternalLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const isLoading = externalLoading || internalLoading;
  const isDisabled = disabled || isLoading;

  const handleClick = async () => {
    if (requireConfirmation) {
      setShowConfirmation(true);
      return;
    }
    
    await executeAction();
  };

  const executeAction = async () => {
    if (!onClick) return;

    try {
      setInternalLoading(true);
      await onClick();
      
      if (showSuccessState) {
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
      }
    } catch (error) {
      console.error('Smart button action failed:', error);
    } finally {
      setInternalLoading(false);
    }
  };

  const handleConfirm = async () => {
    setShowConfirmation(false);
    await executeAction();
  };

  const getButtonContent = () => {
    if (showSuccess) {
      return (
        <div className="flex items-center gap-2">
          <SuccessIcon className="h-4 w-4" />
          {successMessage || "تم بنجاح"}
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="flex items-center gap-2">
          <LoadingIcon className="h-4 w-4 animate-spin" />
          جاري التحميل...
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4" />}
        {children}
      </div>
    );
  };

  const getButtonVariant = () => {
    if (showSuccess) return "default";
    return variant;
  };

  return (
    <>
      <Button
        variant={getButtonVariant()}
        size={size}
        disabled={isDisabled}
        onClick={handleClick}
        className={cn(
          "transition-all duration-200",
          showSuccess && "bg-success text-success-foreground hover:bg-success/90",
          isLoading && "cursor-wait",
          className
        )}
      >
        {getButtonContent()}
      </Button>

      <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning" />
              {confirmationTitle}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmationDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirm}
              className={cn(
                variant === "destructive" && "bg-destructive text-destructive-foreground hover:bg-destructive/90"
              )}
            >
              تأكيد
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// مكونات متخصصة
export const DeleteButton: React.FC<Omit<SmartButtonProps, 'variant' | 'icon' | 'confirmationTitle' | 'confirmationDescription'> & {
  itemName?: string;
}> = ({ itemName = "العنصر", ...props }) => (
  <SmartButton
    variant="destructive"
    icon={Trash2}
    requireConfirmation
    confirmationTitle="حذف العنصر"
    confirmationDescription={`هل أنت متأكد من حذف ${itemName}؟ لا يمكن التراجع عن هذا الإجراء.`}
    successMessage="تم الحذف"
    showSuccessState
    {...props}
  />
);

export const SaveButton: React.FC<Omit<SmartButtonProps, 'icon' | 'successMessage'>> = (props) => (
  <SmartButton
    icon={Check}
    successMessage="تم الحفظ"
    showSuccessState
    {...props}
  />
);